import { SettingFieldComponent } from 'src/app/components/setting/setting-field/setting-field.component';
import { ModalController } from '@ionic/angular';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { PrivilegeService } from 'src/app/services/account/privilege/privilege.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { SettingVisitorComponent } from 'src/app/components/setting/setting-visitor/setting-visitor.component';
import { SettingCheckinComponent } from 'src/app/components/setting/setting-checkin/setting-checkin.component';
import { SettingGiftComponent } from 'src/app/components/setting/setting-gift/setting-gift.component';

import { VisitorSettingType } from 'src/app/types/visitor';
import { ExportType, ModuleType, SettingFieldType } from 'src/app/types/general';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { AccountDetailType, AccountSettingType } from 'src/app/types/account';
import { GuestExportService } from 'src/app/services/guest/guest-export.service';
import { SettingSeatingComponent } from 'src/app/components/setting/setting-seating/setting-seating.component';

import { AccountInfoService } from 'src/app/services/account/account-info.service';

import { AccountInfo } from 'src/app/interfaces/account';
import { SettingVisitorWizardComponent } from 'src/app/components/setting/setting-visitor-wizard/setting-visitor-wizard.component';
import { AccountEventModeService } from 'src/app/services/account/account-event-mode.service';
import { CurrentPrivilege } from 'src/app/interfaces/privilege';
import { ExportQrcodeComponent } from 'src/app/components/qrcode/export-qrcode/export-qrcode.component';
import { SupportAdminService } from 'src/app/services/support/support-admin.service';
import { DebugModeService } from 'src/app/services/general/debug-mode.service';
import { VisitorModeService } from 'src/app/services/visitor/visitor-mode.service';
import { VisitorService } from 'src/app/services/visitor/visitor.service';
import { PageData } from 'src/app/interfaces/visitor';
import { VisitorManageService } from 'src/app/services/visitor/visitor-manage.service';
import { WebsiteModeUpgradeService } from 'src/app/services/website/website-mode-upgrade.service';
import { WebsiteDetailService } from 'src/app/services/website/website-detail.service';
import { WebsiteDetail } from 'src/app/interfaces/website';

/**
 * Account setting page
 */
@Component({
  selector: 'app-account-setting',
  templateUrl: './account-setting.page.html',
  styleUrls: ['./account-setting.page.scss'],
})
export class AccountSettingPage implements OnInit, OnDestroy  {

  eventMode: boolean;

  debugMode: boolean;

  visitorMode: boolean;

  newWebsiteMode: boolean;

  currentPrivilege: CurrentPrivilege;

  private accountInfoSubscription: Subscription;

  private privilegeSubscription: Subscription;

  private visitorSubscription: Subscription;

  private websiteDetailSubsciption: Subscription;

  /**
   * Constructor
   * @param router router
   * @param modalController modal controller
   * @param translate translate
   * @param seatingService seating service
   * @param seatingManageService seating manage service
   * @param privilegeService privilege service
   * @param popupService popup service
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private modalController: ModalController,
    private privilegeService: PrivilegeService,
    private accountEventModeService: AccountEventModeService,
    private accountInfoService: AccountInfoService,
    private guestExportService: GuestExportService,
    private supportAdminService: SupportAdminService,
    private visitorService: VisitorService,
    private visitorModeService: VisitorModeService,
    private websiteDetailService: WebsiteDetailService,
    private websiteModeUpgradeService: WebsiteModeUpgradeService,
    private debugModeService: DebugModeService,
    private popupService: PopupService,
  ) { }

  ngOnInit(): void {
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.unwatch();
  }

  /**
   * Before view enter
   */
  ionViewWillEnter() {
    this.debugMode = this.debugModeService.debugMode || this.supportAdminService.checkAdmin() ? true : false;
    this.watch();
  }

  /**
   * Before view leave
   */
  ionViewWillLeave() {
    this.unwatch();
  }

  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;

      if (state?.settingType) {
        this.checkSettingType(state.settingType);
      } else if (param?.settingType) {
        this.checkSettingType(param.settingType);
      }
    });
  }

  watch() {
    this.watchAccountInfo();
    this.watchPrivilege();
    this.watchVisitor();
    this.watchWebsiteDetail();
  }

  unwatch() {
    this.unwatchAccounInfo();
    this.unwatchPrivilege();
    this.unwatchVisitor();
    this.unwatchWebsiteDetail();
  }

  async watchVisitor() {
    if (!this.visitorSubscription) {
      this.visitorSubscription = this.visitorService.observablePageData.subscribe((pageData: PageData) => {
        this.newWebsiteMode = this.visitorModeService.checkNewWebsite();
        this.visitorMode = this.visitorModeService.checkMode();
      })
    }
  }

  async unwatchVisitor() {
    if (this.visitorSubscription) {
      this.visitorSubscription.unsubscribe();
      this.visitorSubscription = null;
    }
  }

  /**
   * Watch guest per seating
   */
  async watchAccountInfo() {
    if (!this.accountInfoSubscription) {
      this.accountInfoSubscription = this.accountInfoService.observableAccountInfo.subscribe((accountInfo: AccountInfo) => {
        this.eventMode = accountInfo?.eventMode ? true : false;
      })
    }
    ;
  }

  async watchWebsiteDetail() {
    if (!this.websiteDetailSubsciption) {
      this.websiteDetailSubsciption = this.websiteDetailService.observable.subscribe((websiteDetail: WebsiteDetail) => {
        this.newWebsiteMode = this.visitorModeService.checkNewWebsite();
      })
    }
  }

  async unwatchWebsiteDetail() {
    if (this.websiteDetailSubsciption) {
      this.websiteDetailSubsciption.unsubscribe();
      this.websiteDetailSubsciption = null;
    }
  }

  /**
   * Unwatch  guest per seating
   */
  async unwatchAccounInfo() {
    if (this.accountInfoSubscription) {
      this.accountInfoSubscription.unsubscribe();
      this.accountInfoSubscription = null;
    }
  }

  async watchPrivilege() {
    if (!this.privilegeSubscription) {
      this.privilegeSubscription = this.privilegeService.observableCurrentPrivilege.subscribe(() => {
        this.setupPrivilege();
      })
    }
  }

  async unwatchPrivilege() {
    if (this.privilegeSubscription) {
      this.privilegeSubscription.unsubscribe();
      this.privilegeSubscription = null;
    }
  }

  setupPrivilege() {
    if (!this.currentPrivilege) {
      this.currentPrivilege = {};
    }
    this.currentPrivilege = {
      'account': {
        'setting': this.checkPrivilege('account', 'setting'),
      },
      'guest': {
        'view': this.checkPrivilege('guest', 'view'),
        'qrcode': this.checkPrivilege('guest', 'qrcode'),
        'edit': this.checkPrivilege('guest', 'edit'),
      },
      'checkin': {
        'view': this.checkPrivilege('checkin', 'view'),
        'checkin': this.checkPrivilege('checkin', 'checkin'),
      },
      'gift': {
        'edit': this.checkPrivilege('gift', 'edit'),
      },
    };
  }

  checkSettingType(settingType: AccountSettingType) {
    if (settingType === 'accont_setting') {
      this.goAccountDetailPage();
    } else if (settingType === 'invited_by') {
      this.presentSettingFieldModal('invited_by');
    } else if (settingType === 'category') {
      this.presentSettingFieldModal('category');
    } else if (settingType === 'dietary_req') {
      this.presentSettingFieldModal('dietary_req');
    } else if (settingType === 'special_req') {
      this.presentSettingFieldModal('special_req');
    } else if (settingType === 'session') {
      this.presentSettingFieldModal('session');
    } else if (settingType === 'seating') {
      this.presentSeatingSettingModal();
    } else if (settingType === 'checkin_setting') {
      this.presentCheckinSettingModal();
    } else if (settingType === 'guest_page_quick_setup') {
      this.presentVisitorSettingWizard();
    } else if (settingType === 'guest_page_info') {
      this.presentVisitorSetting('info');
    } else if (settingType === 'guest_page_setting') {
      this.presentVisitorSetting('setting');
    } else if (settingType === 'guest_page_theme') {
      this.presentVisitorSetting('theme');
    } else if (settingType === 'guest_qrcode') {
      this.export('qrcode');
    }
  }

  /**
   * Check user privilege
   * @param action Action
   */
  checkPrivilege(module: ModuleType, action: string): boolean {
    return this.privilegeService.checkCurrentUserPrivilege(module, action);
  }

  /**
   * Present setting field cmodal
   * @param settingFieldType Setting field type
   */
  async presentSettingFieldModal(settingFieldType: SettingFieldType) {
    const modal = await this.modalController.create({
      component: SettingFieldComponent,
      componentProps: {
        settingMode: true,
        settingFieldType
      }
    });
    modal.present();
  }

  /**
   * Present gift setting
   */
  async presentGiftSetting() {
    const modal = await this.modalController.create({
      component: SettingGiftComponent,
      cssClass: '',
      componentProps: {}
    });
    modal.present();
  }

  /**
   * Present check-in setting
   */
  async presentVisitorSetting(type: VisitorSettingType) {
    if (type) {
      const modal = await this.modalController.create({
        component: SettingVisitorComponent,
        cssClass: 'modal-full-screen-bk',
        componentProps: {
          type
        }
      });
      modal.present();
    }
  }

  async presentVisitorSettingWizard() {
    const modal = await this.modalController.create({
      component: SettingVisitorWizardComponent,
      cssClass: 'modal-full-screen-bk',
      componentProps: {
      }
    });
    modal.present();
  }

  /**
   * Present check-in setting
   */
  async presentCheckinSettingModal() {
    const modal = await this.modalController.create({
      component: SettingCheckinComponent,
      cssClass: '',
      componentProps: {}
    });
    modal.present();
  }

  async presentSeatingSettingModal() {
    const modal = await this.modalController.create({
      component: SettingSeatingComponent,
      cssClass: '',
      componentProps: {}
    });
    modal.present();
  }

  async presentExportQrcodeModal() {
    const modal = await this.modalController.create({
      component: ExportQrcodeComponent,
      cssClass: '',
      componentProps: {}
    });
    modal.present();
  }

  goAccountDetailPage(detailType?: AccountDetailType) {
    const navigationExtras: NavigationExtras = {
      state: {
        settingPage: true,
        detailType
      }
    };
    this.router.navigate(['/account/detail'], navigationExtras);
  }

  export(type: ExportType) {
    this.guestExportService.download(type);
  }

  replaceEventType(text: string) {
    return this.accountEventModeService.replaceEventType(text);
  }

  async setEventMode() {
    if (this.eventMode !== this.accountInfoService.accountInfo?.eventMode) {
      const confirm = await this.popupService.presentConfirm(this.translate.instant('FOR_EVENT.confirm_switch'));
      confirm.onWillDismiss().then(async (result: any) => {
        if (result?.data?.confirm) {
          await this.accountInfoService.updateAccountInfo( { eventMode: this.eventMode });
          this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
        } else {
          this.eventMode = this.accountInfoService.accountInfo?.eventMode;
        }
      });
    }
  }

  async updateNewWebsiteMode(newWebsiteMode: boolean) {
    if (this.visitorService.pageData?.setting?.newWebsiteMode !== newWebsiteMode) {
      await this.websiteModeUpgradeService.upgradeNow(newWebsiteMode);
    }
    
  }

  // goTestPage() {
  //   this.router.navigate(['/website-setting/home']);
  // }

}
